<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import AgentAPI from 'dashboard/api/agents';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const showReactivatePopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const selectedTabIndex = ref(0);
const deactivatedAgents = ref([]);
const isFetchingDeactivated = ref(false);

const tabs = computed(() => [
  { key: 0, name: t('AGENT_MGMT.TABS.ACTIVE') },
  { key: 1, name: t('AGENT_MGMT.TABS.DEACTIVATED') },
]);

const isDeactivatedTab = computed(() => selectedTabIndex.value === 1);

const fetchDeactivatedAgents = async () => {
  isFetchingDeactivated.value = true;
  try {
    const response = await AgentAPI.getDeactivated();
    deactivatedAgents.value = response.data;
  } catch {
    deactivatedAgents.value = [];
  } finally {
    isFetchingDeactivated.value = false;
  }
};

const onClickTabChange = index => {
  selectedTabIndex.value = index;
  if (index === 1) {
    fetchDeactivatedAgents();
  }
};

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);
const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

// Mirrors the cells below: role/status/API are only rendered on the active tab.
const tableHeaders = computed(() =>
  isDeactivatedTab.value
    ? [t('AGENT_MGMT.LIST.NAME'), t('AGENT_MGMT.LIST.ACTIONS')]
    : [
        t('AGENT_MGMT.LIST.NAME'),
        t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL'),
        t('AGENT_MGMT.LIST.STATUS'),
        'API',
        t('AGENT_MGMT.LIST.ACTIONS'),
      ]
);

const displayedAgents = computed(() =>
  isDeactivatedTab.value ? deactivatedAgents.value : agentList.value
);
const isLoading = computed(() =>
  isDeactivatedTab.value
    ? isFetchingDeactivated.value
    : uiFlags.value.isFetching
);

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const disconnectPhone = async agent => {
  loading.value[agent.id] = true;
  try {
    await AgentAPI.disconnectPhone(agent.id);
    useAlert(t('AGENT_MGMT.DISCONNECT_PHONE.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('AGENT_MGMT.DISCONNECT_PHONE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[agent.id] = false;
  }
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};

const reactivateConfirmText = computed(
  () => `${t('AGENT_MGMT.REACTIVATE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const reactivateRejectText = computed(
  () => `${t('AGENT_MGMT.REACTIVATE.CONFIRM.NO')} ${currentAgent.value.name}`
);
const reactivateMessage = computed(() => ` ${currentAgent.value.name}?`);

const openReactivatePopup = agent => {
  showReactivatePopup.value = true;
  currentAgent.value = agent;
};
const closeReactivatePopup = () => {
  showReactivatePopup.value = false;
};
const reactivateAgent = async id => {
  try {
    await AgentAPI.reactivate(id);
    deactivatedAgents.value = deactivatedAgents.value.filter(a => a.id !== id);
    store.dispatch('agents/get');
    showAlertMessage(t('AGENT_MGMT.REACTIVATE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.REACTIVATE.API.ERROR_MESSAGE'));
  }
};
const confirmReactivation = () => {
  loading.value[currentAgent.value.id] = true;
  closeReactivatePopup();
  reactivateAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!displayedAgents.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        feature-name="agents"
      >
        <template #actions>
          <Button
            v-if="!isDeactivatedTab"
            icon="i-lucide-circle-plus"
            :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #preBody>
      <woot-tabs
        class="font-medium [&_.tabs]:p-0 mb-4"
        :index="selectedTabIndex"
        @change="onClickTabChange"
      >
        <woot-tabs-item
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :index="index"
          :name="tab.name"
          :show-badge="false"
        />
      </woot-tabs>
    </template>
    <template #body>
      <table class="divide-y divide-slate-75 dark:divide-slate-700">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right text-n-slate-11 last:ltr:text-right last:rtl:text-left"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr v-for="(agent, index) in displayedAgents" :key="agent.email">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-row items-center gap-4">
                <Thumbnail
                  :src="agent.thumbnail"
                  :username="agent.name"
                  size="40px"
                  :status="
                    isDeactivatedTab ? undefined : agent.availability_status
                  "
                />
                <div>
                  <span class="block font-medium capitalize">
                    {{ agent.name }}
                  </span>
                  <span>{{ agent.email }}</span>
                  <div
                    v-if="
                      !isDeactivatedTab &&
                      agent.phone_username &&
                      agent.phone_status
                    "
                    class="queue-status-display mt-1"
                  >
                    <div
                      class="queue-status-bubble phone-status-bubble"
                      :class="{
                        'status-ready': agent.phone_status === 'online',
                        'status-not-ready': agent.phone_status === 'busy',
                        'status-offline': agent.phone_status === 'offline',
                      }"
                    >
                      <fluent-icon icon="phone" size="10" />
                      {{
                        $t(
                          `PHONE_STATUS.${(agent.phone_status || 'offline').toUpperCase()}`
                        )
                      }}
                    </div>
                    <template
                      v-if="
                        agent.phone_queue_status?.length > 0 &&
                        agent.phone_status !== 'offline'
                      "
                    >
                      <div
                        v-for="queue in agent.phone_queue_status"
                        :key="queue.number"
                        v-tooltip="{
                          content: `${queue.name || queue.number}: ${$t(
                            `QUEUE_STATUS.${queue.status}`
                          )}`,
                        }"
                        class="queue-status-bubble"
                        :class="{
                          'status-ready': queue.status === 'READY',
                          'status-break': queue.status === 'BREAK',
                          'status-lunch': queue.status === 'LUNCH',
                          'status-not-ready': ['NOT_READY', 'WRAP_UP'].includes(
                            queue.status
                          ),
                          'status-on-call': [
                            'OTHER_CALL',
                            'QUEUE_CALL',
                          ].includes(queue.status),
                          'status-offline': ['ONLINE', 'OFFLINE'].includes(
                            queue.status
                          ),
                        }"
                      >
                        <span class="truncate-text">
                          {{ queue.name || queue.number }}
                        </span>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </td>

            <td
              v-if="!isDeactivatedTab"
              class="relative py-4 ltr:pr-4 rtl:pl-4"
            >
              <span
                class="block font-medium w-fit"
                :class="{
                  'hover:text-gray-900 group cursor-pointer':
                    agent.custom_role_id,
                }"
              >
                {{ getAgentRoleName(agent) }}

                <div
                  class="absolute left-0 z-10 hidden max-w-[300px] w-auto bg-white rounded-xl border border-slate-50 shadow-lg top-14 md:top-12 dark:bg-slate-800 dark:border-slate-700"
                  :class="{ 'group-hover:block': agent.custom_role_id }"
                >
                  <div class="flex flex-col gap-1 p-4">
                    <span class="font-semibold">
                      {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                    </span>
                    <ul class="pl-4 mb-0 list-disc">
                      <li
                        v-for="permission in getAgentRolePermissions(agent)"
                        :key="permission"
                        class="font-normal"
                      >
                        {{
                          $t(
                            `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                          )
                        }}
                      </li>
                    </ul>
                  </div>
                </div>
              </span>
            </td>
            <td v-if="!isDeactivatedTab" class="py-4 ltr:pr-4 rtl:pl-4">
              <span v-if="agent.confirmed">
                {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
              </span>
              <span v-if="!agent.confirmed">
                {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
              </span>
            </td>
            <td v-if="!isDeactivatedTab" class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                v-if="agent.api_access_allowed"
                v-tooltip.top="$t('AGENT_MGMT.LIST.API_ALLOWED')"
                class="i-lucide-circle-check size-4 text-n-teal-11"
              />
            </td>
            <td class="py-4">
              <div class="flex justify-end gap-1">
                <template v-if="!isDeactivatedTab">
                  <Button
                    v-if="
                      agent.phone_username &&
                      agent.phone_status &&
                      agent.phone_status !== 'offline'
                    "
                    v-tooltip.top="
                      $t('AGENT_MGMT.DISCONNECT_PHONE.BUTTON_TEXT')
                    "
                    icon="i-lucide-phone-off"
                    xs
                    ruby
                    faded
                    :is-loading="loading[agent.id]"
                    @click="disconnectPhone(agent)"
                  />
                  <Button
                    v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                    icon="i-lucide-pen"
                    slate
                    xs
                    faded
                    @click="openEditPopup(agent)"
                  />
                  <Button
                    v-if="showDeleteAction(agent)"
                    v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                    icon="i-lucide-user-x"
                    xs
                    ruby
                    faded
                    :is-loading="loading[agent.id]"
                    @click="openDeletePopup(agent, index)"
                  />
                </template>
                <Button
                  v-else
                  v-tooltip.top="$t('AGENT_MGMT.REACTIVATE.BUTTON_TEXT')"
                  icon="i-lucide-user-check"
                  xs
                  faded
                  :is-loading="loading[agent.id]"
                  @click="openReactivatePopup(agent)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" @close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" @close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        :api-access-enabled="currentAgent.api_access_allowed"
        :is-self="currentAgent.id === currentUserId"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :description="$t('AGENT_MGMT.DELETE.CONFIRM.DESCRIPTION')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <woot-delete-modal
      v-model:show="showReactivatePopup"
      :on-close="closeReactivatePopup"
      :on-confirm="confirmReactivation"
      :title="$t('AGENT_MGMT.REACTIVATE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.REACTIVATE.CONFIRM.MESSAGE')"
      :message-value="reactivateMessage"
      :confirm-text="reactivateConfirmText"
      :reject-text="reactivateRejectText"
    />
  </SettingsLayout>
</template>

<style scoped>
.queue-status-display {
  margin: 4px 0 0 0;
  display: flex;
  flex-wrap: nowrap;
  gap: 0;
  justify-content: flex-start;
}

.queue-status-bubble {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 6px;
  border-radius: 0;
  font-size: 9px;
  font-weight: 500;
  color: white;
  cursor: default;
  min-width: 20px;
  text-align: center;

  &:not(:first-child) {
    margin-left: 1px;
  }

  &:first-child {
    border-bottom-left-radius: 4px;
    border-top-left-radius: 4px;
  }

  &:last-child {
    border-bottom-right-radius: 4px;
    border-top-right-radius: 4px;
  }

  &.status-ready {
    background-color: #43b581; /* green */
  }

  &.status-not-ready {
    background-color: #faa61a; /* yellow */
  }

  &.status-break {
    background-color: #6c757d; /* gray */
  }

  &.status-lunch {
    background-color: #007bff; /* blue */
  }

  &.status-on-call {
    background-color: #007bff; /* blue */
  }

  &.status-offline {
    background-color: #f04747; /* red */
  }
}

.truncate-text {
  max-width: 10em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.phone-status-bubble {
  min-width: 24px;
}
</style>
